import type { TransportCostAllocationResult } from '../../utils/transportCostAllocation';

// The allocation shown on the transport card is a planning view. It is not the
// ledger that posts PZ/WZ costs. When a selected basis cannot split the whole
// truck cost, rendering the missing share as "0 PLN" is misleading.
export function hasCompleteTransportCostAllocation(
  allocation: { summary: Pick<TransportCostAllocationResult['summary'], 'allocationDifference'> },
): boolean {
  return Math.abs(Number(allocation.summary.allocationDifference) || 0) < 0.005;
}

/**
 * The server panel (`costAllocation` of GET /transport-routes/:id, contract
 * round 6) puts on each cargo line the booked share of its ZK split over that
 * ZK's lines. The rest of the truck (`summary.allocationDifference`) is the
 * stock / ZD part or lines the key cannot weigh, so the panel is not
 * "incomplete" because of it. It is incomplete only when a loaded line's
 * amount is `null` ("nie da się rozdzielić").
 */
export function hasUnknownLineAllocation(
  allocation: Pick<TransportCostAllocationResult, 'items'>,
): boolean {
  return allocation.items.some((item) => item.allocatedTransportCost === null && Number(item.quantity) > 0);
}

/**
 * `goodsValueTotal` of a cargo line is operator input (null = not entered).
 * The server panel echoes a missing value as 0, so the panel reads goods value
 * (and everything derived from it) from the cargo line: missing → "—".
 */
export function hasKnownGoodsValue(cargoItem: { goodsValueTotal?: number | null } | null | undefined): boolean {
  return cargoItem?.goodsValueTotal !== null && cargoItem?.goodsValueTotal !== undefined;
}

type SaveWarningRow = { code?: unknown; label?: unknown; orderId?: unknown; supplierOrderId?: unknown };

function readSaveWarnings(responseData: unknown): SaveWarningRow[] {
  const warnings = (responseData as { allocationWarnings?: unknown } | null)?.allocationWarnings;
  return Array.isArray(warnings) ? warnings.map((warning) => (warning || {}) as SaveWarningRow) : [];
}

function labelsFor(responseData: unknown, code: string): string[] {
  return readSaveWarnings(responseData)
    .filter((warning) => warning.code === code)
    .map((warning) => String(warning.label || warning.orderId || warning.supplierOrderId || ''))
    .filter(Boolean);
}

// Save responses of POST/PUT /transport-routes carry the cost-split warnings of
// that save (backend transportService.attachAllocationWarnings; stripped for
// viewers without transport cost visibility). Returns the business numbers of
// customer orders and stock ZDs whose m²/m³ basis misses product dimensions;
// empty when the response has none.
export function readMissingDimensionLabels(responseData: unknown): string[] {
  return labelsFor(responseData, 'MISSING_DIMENSIONS');
}

/** Loads (ZK / ZD numbers) without a goods value under the value key. */
export function readMissingGoodsValueLabels(responseData: unknown): string[] {
  return labelsFor(responseData, 'MISSING_GOODS_VALUE');
}

/** Loads with a product counted only in pieces under the m² / m³ key. */
export function readCountedInPiecesOnlyLabels(responseData: unknown): string[] {
  return labelsFor(responseData, 'COUNTED_IN_PIECES_ONLY');
}

/** szt. key over different units: the split weighed pieces. */
export function readMixedUnitsWarning(responseData: unknown): boolean {
  return readSaveWarnings(responseData).some((warning) => warning.code === 'MIXED_UNITS');
}
